import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import PandaMark from './brand/PandaMark';
import { preloadRoute } from '../lazyRoutes';

const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/apps', label: 'Our Apps' },
  { to: '/crafted', label: 'Crafted' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 68px;
  z-index: 9999;
  display: flex;
  align-items: center;
  background: ${({ $scrolled }) =>
    $scrolled ? 'rgba(250, 248, 242, 0.97)' : 'rgba(250, 248, 242, 0.9)'};
  backdrop-filter: saturate(160%) blur(14px);
  -webkit-backdrop-filter: saturate(160%) blur(14px);
  border-bottom: 1px solid ${({ $scrolled }) => ($scrolled ? 'var(--hairline)' : 'rgba(22,21,15,0.06)')};
  box-shadow: ${({ $scrolled }) => ($scrolled ? '0 6px 24px rgba(22,21,15,0.06)' : 'none')};
  transition: background .3s ease, border-color .3s ease, box-shadow .3s ease;
`;

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 90%;
  max-width: 1200px;
  margin: 0 auto;
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: var(--ink);
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 1.18rem;
  letter-spacing: -0.02em;
`;

// No tile, border or shadow. The old 36px rounded-square plate is what made
// the mark read as a "squarish icon" - the panda is already a circle.
const Mark = styled.div`
  display: grid;
  place-items: center;
  /* 34px sat too small against the 1.18rem wordmark. 42px reads as a peer to
     the type and still stays under the 48px motion floor, so the nav mark
     remains static on every page. */
  width: 42px;
  height: 42px;
  /* Static mark: below the 48px motion floor every animation is killed
     anyway, so mounting PandaMarkLive here only bought a rAF loop, two
     media-query listeners and a pile of timers for motion nobody can see. */
  svg { display: block; }
`;

const Links = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  @media (max-width: 860px) { display: none; }
`;

const NavItem = styled(Link)`
  position: relative;
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 0.96rem;
  color: ${({ $active }) => ($active ? 'var(--ink)' : 'var(--ink-soft)')};
  padding: 0.25rem 0;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: -3px;
    height: 2px;
    width: 100%;
    background: var(--bamboo);
    border-radius: 2px;
    transform: ${({ $active }) => ($active ? 'scaleX(1)' : 'scaleX(0)')};
    transform-origin: left;
    transition: transform .24s var(--ease);
  }
  &:hover { color: var(--ink); }
  &:hover::after { transform: scaleX(1); }
`;

const MenuButton = styled.button`
  display: none;
  background: transparent;
  color: var(--ink);
  font-size: 1.4rem;
  line-height: 1;
  min-width: 44px;
  min-height: 44px;
  padding: 0.35rem 0.5rem;
  border-radius: 8px;
  @media (max-width: 860px) { display: grid; place-items: center; }
`;

// Plain CSS rather than framer-motion. This was the only thing pulling
// framer into the eagerly-loaded bundle, where it cost every route on the site
// ~25KB gzipped for one hamburger sheet. Kept mounted and toggled so the exit
// transition still plays, which is what AnimatePresence was providing.
const Sheet = styled.div`
  position: fixed;
  top: 68px;
  left: 0;
  right: 0;
  background: var(--cream);
  border-bottom: 1px solid var(--hairline);
  box-shadow: 0 18px 40px rgba(22,21,15,0.10);
  display: flex;
  flex-direction: column;
  padding: 1rem 0 1.5rem;
  z-index: 9998;

  transform: translateY(${({ $open }) => ($open ? '0' : '-12px')});
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1),
              opacity 220ms cubic-bezier(0.2, 0.8, 0.2, 1),
              visibility 0s linear ${({ $open }) => ($open ? '0s' : '220ms')};

  @media (prefers-reduced-motion: reduce) {
    transition: visibility 0s;
  }
`;

const SheetLink = styled(Link)`
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 1.05rem;
  color: var(--ink);
  padding: 0.9rem 7.5%;
  border-bottom: 1px solid var(--hairline);

  &:last-child { border-bottom: none; }
`;

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const menuButtonRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  // Escape closes the sheet and hands focus back to the control that opened it.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      menuButtonRef.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const isActive = (to) => (to === '/' ? pathname === '/' : pathname.startsWith(to));

  return (
    <Nav $scrolled={scrolled}>
      <Container>
        <Brand to="/">
          <Mark aria-hidden="true">
            {/* Decorative: the adjacent "Panda Taps" text already labels the link.
                34px is above the motion floor (TIMING.minSize, 32), so this
                blinks, twitches and breathes like the big mark. Eye tracking is
                the one behaviour it does not get - see TIMING.trackAbove. */}
            <PandaMark size={42} ink="var(--ink)" />
          </Mark>
          Panda Taps
        </Brand>

        <Links>
          {NAV_LINKS.map((l) => (
            <NavItem
              key={l.to}
              to={l.to}
              $active={isActive(l.to)}
              onMouseEnter={() => preloadRoute(l.to)}
              onFocus={() => preloadRoute(l.to)}
            >
              {l.label}
            </NavItem>
          ))}
        </Links>

        <MenuButton
          ref={menuButtonRef}
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          aria-expanded={open}
          aria-controls="mobile-nav-sheet"
        >
          {open ? '✕' : '☰'}
        </MenuButton>
      </Container>

      <Sheet id="mobile-nav-sheet" $open={open} aria-hidden={!open}>
        {NAV_LINKS.map((l) => (
          <SheetLink
            key={l.to}
            to={l.to}
            tabIndex={open ? 0 : -1}
            onClick={() => setOpen(false)}
            onMouseEnter={() => preloadRoute(l.to)}
            onFocus={() => preloadRoute(l.to)}
          >
            {l.label}
          </SheetLink>
        ))}
      </Sheet>
    </Nav>
  );
};

export default Navbar;
